import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Calendar, Eye, Info } from 'lucide-react';
import { format, isValid } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Button } from '../Components/ui/button.tsx';
import { Input } from '../Components/ui/input.tsx';
import { Card, CardContent } from '../Components/ui/card.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../Components/ui/table.tsx';
import { EnhancedToggle } from '../Components/ui/enhanced-toggle.tsx';
import { useReportIds } from '../hooks/useReportIds.js';
import { useAxios } from '../Components/axiosContext.js';
import Pagination from '../Components/pagination.jsx';
import { FollowupSkeleton } from '../Components/skeleton.jsx';
import { MissedCallCard } from '../Components/MissedCallCard.tsx';
import { CheckInSchedule } from '../Components/CheckInSchedule.tsx';

// Helper function to convert 24-hour time to 12-hour AM/PM format
const formatTimeToAMPM = (time) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

const HealthJournal = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('Intake');
  const { reportIds, reportDates, isLoading } = useReportIds();
  const { user } = useUser();
  const axios = useAxios();
  const navigate = useNavigate();

  // Checkins-specific state
  const [checkinsData, setCheckinsData] = useState([]);
  const [checkinsTotal, setCheckinsTotal] = useState(0);
  const [checkinsLoading, setCheckinsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMissedCalls, setHasMissedCalls] = useState(false);
  const [isLoadingMissedCalls, setIsLoadingMissedCalls] = useState(true);
  const itemsPerPage = 5;

  // Function to check for missed calls
  const checkMissedCalls = async () => {
    try {
      setIsLoadingMissedCalls(true);

      const response = await axios.get('/calls/missed-check');

      if (response.data.success) {
        setHasMissedCalls(response.data.data.hasMissedCalls);
      }
    } catch (error) {
      console.error('Failed to check missed calls:', error);
      // On error, assume no missed calls
      setHasMissedCalls(false);
    } finally {
      setIsLoadingMissedCalls(false);
    }
  };

  // Check for missed calls on component mount
  useEffect(() => {
    if (user?.id && axios) {
      checkMissedCalls();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Fetch checkins data when selectedType is 'Checkins' or page changes
  useEffect(() => {
    if (selectedType === 'Checkins' && user?.id && axios) {
      const fetchCheckinsData = async () => {
        const offset = (currentPage - 1) * itemsPerPage;
        setCheckinsLoading(true);
        try {
          const response = await axios.get('calls/reports', {
            params: {
              limit: itemsPerPage,
              offset,
              reportType: 'FOLLOWUP',
              order_by: 'createdAt',
              order: 'desc',
            },
          });
          if (response?.data?.data) {
            setCheckinsData(response.data.data);
          }
          setCheckinsTotal(response.data.total);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching checkins:', error.response?.data || error.message);
          setCheckinsData([]);
        } finally {
          setCheckinsLoading(false);
        }
      };
      fetchCheckinsData();
    }
  }, [selectedType, user?.id, axios, currentPage]);

  // Reset pagination when switching between types
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedType]);

  // Generate intake reports from real data
  const intakeReports = (() => {
    const reports = [];

    // Session 0: final Intake (if it exists)
    if (reportIds.INTAKE && reportDates.INTAKE) {
      const date = new Date(reportDates.INTAKE);
      reports.push({
        date: format(date, 'MMM d, yyyy'),
        time: format(date, 'HH:mm:ss'),
        title: 'Final Intake',
        reportId: reportIds.INTAKE,
        sessionNumber: 0,
      });
    }

    // Session 1: Initial Health Assessment
    if (reportIds.INTAKE1 && reportDates.INTAKE1) {
      const date = new Date(reportDates.INTAKE1);
      reports.push({
        date: format(date, 'MMM d, yyyy'),
        time: format(date, 'HH:mm:ss'),
        title: 'Initial Health Assessment',
        reportId: reportIds.INTAKE1,
        sessionNumber: 1,
      });
    }

    // Session 2: Wellness Planning
    if (reportIds.INTAKE2 && reportDates.INTAKE2) {
      const date = new Date(reportDates.INTAKE2);
      reports.push({
        date: format(date, 'MMM d, yyyy'),
        time: format(date, 'HH:mm:ss'),
        title: 'Wellness Planning Session',
        reportId: reportIds.INTAKE2,
        sessionNumber: 2,
      });
    }

    // Session 3: Action Plan Development
    if (reportIds.INTAKE3 && reportDates.INTAKE3) {
      const date = new Date(reportDates.INTAKE3);
      reports.push({
        date: format(date, 'MMM d, yyyy'),
        time: format(date, 'HH:mm:ss'),
        title: 'Action Plan Development',
        reportId: reportIds.INTAKE3,
        sessionNumber: 3,
      });
    }

    // Sort by date (most recent first)
    return reports.sort((a, b) => new Date(b.date) - new Date(a.date));
  })();

  // Generate checkins entries from API data
  const checkinsEntries = (() => {
    return checkinsData.map((report) => ({
      date: isValid(new Date(report?.createdAt)) ? format(new Date(report?.createdAt), 'MMM d, yyyy') : 'Unknown Date',
      time: isValid(new Date(report?.createdAt)) ? format(new Date(report?.createdAt), 'HH:mm:ss') : '00:00:00',
      title: `Follow-up Check-in - ${isValid(new Date(report?.createdAt)) ? format(new Date(report?.createdAt), 'MMM d, yyyy') : 'Unknown Date'}`,
      reportId: report?.id,
      type: 'checkin',
    }));
  })();

  const currentEntries = selectedType === 'Intake' ? intakeReports : checkinsEntries;
  const filteredEntries = currentEntries.filter((entry) => {
    const matchesSearch = entry.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Handle view button click for different entry types
  const handleViewReport = (entry) => {
    if (selectedType === 'Intake' && entry.reportId && entry.sessionNumber !== undefined) {
      const basePath = entry.reportId ? `/auth/report/${entry.reportId}` : '/auth/start-report';
      if (entry.sessionNumber === 0) {
        // Final intake - navigate without session number
        navigate(`${basePath}/intake`);
      } else if (entry.sessionNumber) {
        // Other intake sessions - include session number
        navigate(`${basePath}/intake${entry.sessionNumber}`);
      }
    } else if (selectedType === 'Checkins' && entry.reportId) {
      // Navigate to followup page or specific checkin view
      navigate('/auth/health-journal');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {!isLoadingMissedCalls && hasMissedCalls && <MissedCallCard onDismiss={() => setHasMissedCalls(false)} />}
      <div className="bg-white rounded-2xl shadow-lg m-4 sm:m-6 p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header Section */}
        <motion.div
          initial={{
            opacity: 0,
            y: -20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.5,
          }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-hana-light-green rounded-2xl">
                <Calendar className="h-6 w-6 text-hana-dark-green" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-hana-dark-green">Health Journal</h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">Track your daily health patterns, mood, and progress over time</p>
              </div>
            </div>
          </div>
          {/* <Button className="sm:w-auto w-full rounded-xl bg-hana-green hover:bg-hana-green/90 text-white">
            <Plus className="h-4 w-4 mr-1" />
            New Entry
          </Button> */}
        </motion.div>

        {/* Information Section */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          <Card className="rounded-2xl border-0 shadow-lg bg-hana-light-green/20 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="pt-1 bg-hana-light-green rounded-xl mt-0.5">
                  <Info className="h-4 w-4 text-hana-dark-green" />
                </div>
                <p className="pt-1 text-sm text-hana-dark-green/80 leading-relaxed">
                  These journal entries automatically update your dashboard summary, helping us track your progress and provide better support.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
        {/* Check In Schedule Section */}
        <CheckInSchedule />

        {/* Journal Entries Section */}
        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.7,
            delay: 0.2,
          }}
          className="space-y-4"
        >
          {/* Section Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className="text-xl sm:text-2xl font-semibold text-hana-dark-green">Journal Entries</h2>
          </div>

          {/* Search Controls */}
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search journal entries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl border-0 bg-muted/50"
              />
            </div>

            {/* Enhanced Toggle Selector */}
            <EnhancedToggle
              options={[
                { value: 'Intake', label: 'Intake' },
                { value: 'Checkins', label: 'Checkins' },
              ]}
              value={selectedType}
              onChange={setSelectedType}
              size="sm"
              variant="hana"
              ariaLabel="Journal entry type selector"
              className="min-w-[160px] sm:min-w-[180px]"
            />
          </div>

          {/* Content Area with ARIA */}
          <div id="journal-content" role="tabpanel" aria-labelledby="journal-entries">
            {/* Show loading skeleton only in content area */}
            {(isLoading && selectedType === 'Intake') || (checkinsLoading && selectedType === 'Checkins') ? (
              <Card className="rounded-2xl border-0 shadow-lg bg-card/50 backdrop-blur-sm">
                <CardContent className="p-6">
                  <FollowupSkeleton />
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block mt-3">
                  <Card className="rounded-2xl border-0 shadow-lg bg-card/50 backdrop-blur-sm">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-semibold">Date & Time</TableHead>
                          <TableHead className="font-semibold">Title</TableHead>
                          <TableHead className="font-semibold text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredEntries.map((entry, index) => (
                          <TableRow key={entry.reportId || index} className="hover:bg-muted/50">
                            <TableCell>
                              <div className="space-y-1">
                                <div className="font-medium text-sm">{entry.date}</div>
                                <div className="text-xs text-muted-foreground">{formatTimeToAMPM(entry.time)}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <p className="font-medium text-sm leading-relaxed">{entry.title}</p>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-xl text-hana-green hover:bg-hana-green/10"
                                onClick={() => handleViewReport(entry)}
                              >
                                <Eye className="h-4 w-4" />
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden space-y-4 mt-3">
                  {filteredEntries.map((entry, index) => (
                    <Card key={entry.reportId || index} className="rounded-2xl border-0 shadow-lg bg-card/50 backdrop-blur-sm">
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {/* Date */}
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-sm">{entry.date}</div>
                              <div className="text-xs text-muted-foreground">{formatTimeToAMPM(entry.time)}</div>
                            </div>
                          </div>

                          {/* Title */}
                          <p className="font-medium text-sm leading-relaxed">{entry.title}</p>

                          {/* Action */}
                          <div className="flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="rounded-xl text-hana-green hover:bg-hana-green/10"
                              onClick={() => handleViewReport(entry)}
                            >
                              <Eye className="h-4 w-4" />
                              View
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* No Results */}
                {filteredEntries.length === 0 && !checkinsLoading && !isLoading && (
                  <Card className="rounded-2xl border-0 mt-2 shadow-lg bg-card/50 backdrop-blur-sm">
                    <CardContent className="text-center py-12">
                      <div className="text-muted-foreground">
                        {selectedType === 'Checkins' ? 'No check-in entries found.' : 'No journal entries found matching your criteria.'}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Pagination for Checkins */}
                {selectedType === 'Checkins' && checkinsData.length > 0 && (
                  <div className="flex justify-center mt-6">
                    <Pagination
                      totalItems={checkinsTotal}
                      itemsPerPage={itemsPerPage}
                      currentPage={currentPage}
                      onPageChange={(page) => setCurrentPage(page)}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
export default HealthJournal;
